import type { PropsWithChildren, ReactNode } from 'react';
import { Children } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Marquee } from 'atoms/container/Marquee';
import { Container } from 'atoms/layout/Container';
import { Vertical } from 'atoms/layout/flex';
import { BodyL, DisplayL } from 'atoms/typography';
import type { Pictures } from 'components/landing/types';
import { useConnectionContext } from 'contexts/connection';
import { laptopAndAbove } from 'style/mediaQuery';

const ImageWrapper = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background-image: var(--background);
  background-size: cover;
  background-position: center bottom;
  border-radius: 20px 20px 0 0;
  min-height: 560px;
  margin-bottom: calc(-1 * var(--quadruple-unit));
  padding-bottom: var(--quadruple-unit);
  &::before {
    position: absolute;
    inset: 0;
    content: '';
    background-image: linear-gradient(
        to top,
        var(--c-black),
        var(--c-black) 5%,
        transparent 40%
      ),
      linear-gradient(to right, rgba(var(--c-rgb-black), 0.5), transparent 20%);
  }
`;
const Content = styled(Vertical)`
  position: relative;
  text-align: left;
  max-width: 380px;
  width: 100%;
  margin: auto;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    position: absolute;
    margin-left: 10%;
    align-items: flex-start;
  }
`;
const LeagueImageWrapper = styled.button`
  border-radius: 20px;
  overflow: hidden;
  width: 300px;
`;

type ScoutAssets = {
  background: Pictures;
  players: Pictures;
};

type Props = {
  assets: ScoutAssets;
  title: ReactNode;
  description: ReactNode;
  ctaLabel: ReactNode;
  players?: ReactNode;
};
export const Scout = ({
  assets,
  children,
  title,
  description,
  ctaLabel,
  players,
}: PropsWithChildren<Props>) => {
  const { signUp } = useConnectionContext();
  return (
    <Vertical
      gap={0}
      style={
        {
          '--background': `image-set(url(${assets.background.avif}) type('image/avif'), url(${assets.background.pictureUrl}) type('image/webp'))`,
        } as CSSProperties
      }
    >
      <Container>
        <ImageWrapper onClick={signUp}>
          <Content gap={5}>
            <Vertical gap={3}>
              <DisplayL>{title}</DisplayL>
              <BodyL>{description}</BodyL>
            </Vertical>
            <Button asSpan color="secondary" size="large">
              {ctaLabel}
            </Button>
          </Content>
          {players}
        </ImageWrapper>
      </Container>
      <Marquee direction="reverse">
        {Children.toArray(children).map((child, index) => (
          <LeagueImageWrapper
            type="button"
            onClick={signUp}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
          >
            {child}
          </LeagueImageWrapper>
        ))}
      </Marquee>
    </Vertical>
  );
};
